/**
 * Saved views: the things the sidebar lists.
 *
 * A view is a named set of list parameters, and the URL carries its id. Filters
 * chosen in the filter bar narrow whatever the view produced, so a view is a
 * starting point rather than a fixed query -- which is what lets "All open"
 * plus a pipeline filter stand in for a dozen hand-written views.
 */

import type { TicketListParams } from "./api";
import type { TicketStatus } from "../types";

/** Mirrors OPEN_STATUSES / CLOSED_STATUSES on the API. */
export const OPEN_STATUSES: TicketStatus[] = ["open"];
export const CLOSED_STATUSES: TicketStatus[] = ["closed"];

export type ViewId =
  | "unassigned"
  | "mine"
  | "mentions"
  | "my-team"
  | "open"
  | "overdue"
  | "drafts"
  | "spam"
  | "closed"
  /** One per agent: `user-12`. Everyone can open everyone's. */
  | `user-${number}`
  /** A saved view: `saved-3`. Its filters are resolved server-side. */
  | `saved-${number}`;

export interface ViewDefinition {
  id: ViewId;
  label: string;
}

/** The fixed views, in sidebar order. */
export const MAIN_VIEWS: ViewDefinition[] = [
  { id: "unassigned", label: "Unassigned" },
  { id: "mine", label: "My open tickets" },
  { id: "mentions", label: "My mentions" },
  { id: "my-team", label: "My team's open tickets" },
  { id: "open", label: "All open tickets" },
  /*
   * Directly under "All open", because that is what it is a slice of: the same
   * queue, cut to the tickets already past their response target. Saved views
   * could always express this, which meant every desk re-created it by hand
   * and each one drew the line somewhere slightly different.
   */
  { id: "overdue", label: "Overdue" },
  /*
   * Unsent work, and the only view whose membership this browser decides. The
   * ids are supplied by the caller -- see `paramsForView` -- but everything
   * else about it is an ordinary view, which is the point: a draft is on a
   * ticket, and a list of them should be the queue it is a slice of rather
   * than a second kind of list to learn.
   */
  { id: "drafts", label: "Drafts" },
  { id: "spam", label: "Spam" },
  { id: "closed", label: "All closed tickets" },
];

/** Extract the agent id from a per-user view, or null for the fixed views. */
export function userIdFromView(view: ViewId): number | null {
  const match = /^user-(\d+)$/.exec(view);
  return match ? Number(match[1]) : null;
}

/** Extract the saved-view id, or null for anything else. */
export function savedViewIdFrom(view: ViewId): number | null {
  const match = /^saved-(\d+)$/.exec(view);
  return match ? Number(match[1]) : null;
}

export function isViewId(value: string): value is ViewId {
  return (
    MAIN_VIEWS.some((view) => view.id === value) ||
    /^user-\d+$/.test(value) ||
    /^saved-\d+$/.test(value)
  );
}

/**
 * The list parameters a view stands for.
 *
 * Every view except "All closed" is scoped to open work: an agent opening
 * "Unassigned" wants things needing an owner, not a history of solved tickets.
 */
export function paramsForView(view: ViewId): TicketListParams {
  const savedId = savedViewIdFrom(view);
  if (savedId !== null) {
    /*
     * Only the id travels. The filters live on the server, so editing a view
     * takes effect everywhere at once and a client cannot apply one it is not
     * allowed to see.
     */
    return { savedView: savedId };
  }

  const userId = userIdFromView(view);
  if (userId !== null) {
    return { status: OPEN_STATUSES, assignee: userId };
  }

  switch (view) {
    case "unassigned":
      return { status: OPEN_STATUSES, assignee: "unassigned" };
    case "mine":
      return { status: OPEN_STATUSES, scope: "me" };
    case "mentions":
      /*
       * Deliberately not restricted to open tickets. Being named in a note is
       * a request for your attention, and a closed ticket you were asked about
       * is exactly the kind of thing that would otherwise be missed.
       */
      return { scope: "mentions" };
    case "my-team":
      return { status: OPEN_STATUSES, scope: "my-team" };
    case "spam":
      /*
       * The only view that asks for spam. Every other list hides it, which is
       * what makes this one the place to check whether the filter has taken
       * something it should not have.
       */
      return { includeSpam: true, spamOnly: true };
    case "overdue":
      /*
       * Breached only, not "at risk". A view that mixed the two would answer
       * "what have we missed" with a list that is mostly things we have not,
       * and the answer to the second question is a sort on the SLA column,
       * which this list already offers.
       *
       * Open, like every other view here: a closed ticket that was answered
       * late is a reporting question, not a queue.
       */
      return { status: OPEN_STATUSES, slaState: "breached" };
    case "drafts":
      /*
       * Deliberately empty, and deliberately not open-only.
       *
       * The ids are local to the browser, so this function -- which maps a
       * view to a question the server can answer -- cannot fill them in. The
       * caller overrides `ids` with what the draft store holds; an empty array
       * reaching the server matches nothing, which is the right answer for an
       * agent with no drafts.
       *
       * No status restriction because chasing a *closed* ticket is the case
       * the drafts view was asked for by name.
       */
      return { ids: [] };
    case "closed":
      return { status: CLOSED_STATUSES };
    case "open":
    default:
      return { status: OPEN_STATUSES };
  }
}

export function labelForView(
  view: ViewId,
  users: { id: number; name: string }[],
  saved: { id: number; name: string }[] = [],
): string {
  const savedId = savedViewIdFrom(view);
  if (savedId !== null) {
    return saved.find((entry) => entry.id === savedId)?.name ?? "Saved view";
  }

  const userId = userIdFromView(view);
  if (userId !== null) {
    return users.find((user) => user.id === userId)?.name ?? "Agent";
  }
  return MAIN_VIEWS.find((entry) => entry.id === view)?.label ?? "Tickets";
}

/** Where the Helpdesk opens for anybody who has not chosen otherwise. */
export const DEFAULT_LANDING_VIEW: ViewId = "open";

/**
 * The views somebody may choose to land on: every preset but Drafts.
 *
 * Drafts is left out because its membership is this browser's local storage,
 * so as a landing page it would be empty on every other device -- an empty
 * list that looks like a broken one. Mirrors `isDefaultViewKey` on the API.
 */
export const LANDING_PRESETS: ViewDefinition[] = MAIN_VIEWS.filter(
  (view) => view.id !== "drafts",
);

/**
 * The view the Helpdesk opens on, from what this agent chose.
 *
 * Resolved here rather than trusted as stored because the choice can outlive
 * what it names: a saved view deleted after somebody made it their landing
 * page would otherwise greet them every morning with "That view does not
 * exist". Falling back to All open is the honest answer -- it is what they had
 * before choosing -- and the setting is left alone, so a view restored under
 * the same id would come back by itself.
 *
 * `savedIds` is undefined while the list is loading, and a saved view is then
 * taken on trust rather than waited for: the list has nearly always been
 * fetched by the sidebar already, and holding the whole Helpdesk on it for the
 * rare deleted view would slow down the common case to protect the odd one.
 */
export function landingView(
  chosen: string | null | undefined,
  savedIds: number[] | undefined,
): ViewId {
  if (!chosen || !isViewId(chosen) || chosen === "drafts") {
    return DEFAULT_LANDING_VIEW;
  }

  const savedId = savedViewIdFrom(chosen);
  if (savedId !== null && savedIds && !savedIds.includes(savedId)) {
    return DEFAULT_LANDING_VIEW;
  }

  return chosen;
}
